// pages/machine-settings.js — lifecycle hook for the dedicated Settings page
// (data-page="machine-settings").
//
// This is the "where are my settings" fix: instead of a small per-row "Settings"
// button on the Machines page that opens a modal, the full per-machine daemon
// settings form renders INLINE here, grouped into sections (Protection /
// Worktrees / Git context / Coordination / Logging), with a machine selector at
// the top. The form, team-lock 🔒 rendering, and changed-only save all come from
// machine-settings.js (renderMachineSettingsInline) — this module only owns the
// page lifecycle + the machine picker.
//
// Lifecycle pattern mirrors pages/settings.js / pages/machines.js: a
// MutationObserver on the section's `.active` class flip drives the first
// render; stop() is a no-op (the form reacts to user input, doesn't poll).
import { api } from "../api.js";
import { escapeHTML as esc } from "../util.js";
import { renderMachineSettingsInline, renderMachineSettingsPreview } from "../machine-settings.js";
import { probeLocalDaemon, resolveLocalDaemonBase, switchLocalDaemonTeam } from "../daemon-discovery.js";
import { state } from "../state.js";

const SELECT_ID = "machine-settings-picker";
const FORM_ID = "machine-settings-form";

// machineDisplayName mirrors my-machines.js: the worker returns "Unnamed
// machine" when tokens.label is null, so treat that (and empty) as unnamed and
// fall back to a prettified laptop_id rather than showing "Unnamed machine".
function machineDisplayName(m) {
  const isUnnamed = !m.label || m.label === "Unnamed machine";
  return isUnnamed
    ? (m.laptop_id || "Machine").replace(/^device-/, "").replace(/^laptop_/, "")
    : m.label;
}

async function renderMachineSettingsPage() {
  const section = document.querySelector('section.page[data-page="machine-settings"]');
  if (!section) return;
  const host = section.querySelector("#machine-settings-host");
  if (!host) return;

  host.innerHTML = `<div style="color: var(--text-tertiary); font-size: 13px;">Loading machines…</div>`;

  let machines = [];
  try {
    // Response shape: { machines: [...], active_count, ... } — same as
    // my-machines.js consumes. Use data.machines directly.
    const data = await api("/v1/me/machines?limit=100&offset=0");
    machines = (data && data.machines) || [];
    if (!Array.isArray(machines)) machines = [];
  } catch (err) {
    host.innerHTML = `<div style="color: var(--danger); font-size: 13px;">Couldn't load your machines: ${esc((err && err.message) || "unknown")}</div>`;
    return;
  }

  if (machines.length === 0) {
    await renderNoMachine(host);
    return;
  }

  // Default to a running machine if any, else the first.
  const running = machines.find((m) => m.is_running || m.is_active || m.is_online);
  const defaultMachine = running || machines[0];

  const options = machines
    .map((m) => {
      const name = machineDisplayName(m);
      const sel = m.laptop_id === defaultMachine.laptop_id ? " selected" : "";
      return `<option value="${esc(m.laptop_id)}" data-label="${esc(name)}"${sel}>${esc(name)}</option>`;
    })
    .join("");

  // The picker has its own border-bottom as the separator between it and the
  // form below (the form starts with the section-tab bar, which has no top
  // border of its own).
  host.innerHTML = `
    <div style="display: flex; align-items: center; gap: 10px; padding: 16px 20px; border-bottom: 1px solid var(--border);">
      <label for="${SELECT_ID}" style="font-size: 13px; color: var(--text-secondary); font-weight: 600;">Machine</label>
      <select id="${SELECT_ID}" class="setting-select" style="min-width: 200px;">${options}</select>
    </div>
    <div id="${FORM_ID}"></div>`;

  const picker = host.querySelector("#" + SELECT_ID);
  const formEl = host.querySelector("#" + FORM_ID);

  const isOnline = (laptopID) => {
    const m = machines.find((x) => x.laptop_id === laptopID);
    return m ? !!(m.is_running || m.is_active || m.is_online) : undefined;
  };
  const renderFor = (laptopID, label) => renderMachineSettingsInline(formEl, laptopID, label, isOnline(laptopID));

  if (picker) {
    picker.addEventListener("change", () => {
      const opt = picker.options[picker.selectedIndex];
      renderFor(picker.value, opt ? opt.dataset.label : "");
    });
  }
  renderFor(defaultMachine.laptop_id, machineDisplayName(defaultMachine));
}

const NOTICE = "padding: 16px 20px; border-bottom: 1px solid var(--border); font-size: 13px; line-height: 1.6; color: var(--text-secondary);";
const NOTICE_ACTIONS = "display: flex; flex-wrap: wrap; gap: 8px; margin-top: 10px;";

// renderNoMachine explains, for THIS browser's computer, why no machine is
// listed and offers the one action that fixes it, then shows the settings
// read-only so the page is never blank. The list only holds machines linked
// to this login in this team; the local daemon (if reachable) tells us which
// of the two is missing.
async function renderNoMachine(host) {
  // Ask, rather than read state.teamInfo: opening /machine-settings directly
  // runs this before boot has loaded it, which read as "another team".
  const teamInfo = await api("/v1/teams/info").catch(() => null);
  const viewing = teamInfo && teamInfo.team_id;
  await probeLocalDaemon().catch(() => {});
  const base = await resolveLocalDaemonBase().catch(() => "");
  const localTeam = state.localDaemonCurrentTeam || "";
  const localName = state.localDaemonCurrentTeamName || "another team";
  const knowsViewing = viewing && state.localDaemonTeamIDs && state.localDaemonTeamIDs.has(viewing);
  const localSettings = base ? `<a class="btn btn-sm" href="${esc(base)}/ui/settings" target="_blank" rel="noopener">Open this computer's settings</a>` : "";

  let localInfo = null;
  if (base) {
    try {
      const r = await fetch(base + "/v1/info", { mode: "cors", credentials: "omit" });
      if (r.ok) localInfo = await r.json();
    } catch { /* older daemon or blocked: fall through to the team checks */ }
  }

  let notice;
  if (localInfo && localInfo.team_auth_rejected) {
    notice = `<b style="color: var(--text-display);">This computer has lost its connection to your team.</b><br>
      The team no longer accepts its key, so it can't sync or be managed from here. Chats on it are still protected. Run <code>agc login</code> on it to reconnect.
      <div style="${NOTICE_ACTIONS}">${localSettings}</div>`;
  } else if (base && localTeam && viewing && localTeam !== viewing) {
    const sameName = teamInfo && teamInfo.team_name && teamInfo.team_name === localName;
    notice = `<b style="color: var(--text-display);">${sameName
      ? `This computer is connected to a different team that is also called ${esc(localName)}.`
      : `This computer is connected to ${esc(localName)}, not the team you're viewing.`}</b><br>
      Its settings appear here once it's connected to this team.
      <div style="${NOTICE_ACTIONS}">${knowsViewing ? `<button type="button" class="btn btn-sm btn-brand" id="ms-switch-team">Connect this computer to this team</button>` : `<span>Run <code>agc login</code> on it and choose this team.</span>`}${localSettings}</div>`;
  } else if (base && !localTeam) {
    notice = `<b style="color: var(--text-display);">AgentCollision is running on this computer, but it isn't connected to a team.</b><br>
      Run <code>agc login</code> on it and choose this team to manage it from here.
      <div style="${NOTICE_ACTIONS}">${localSettings}</div>`;
  } else if (base) {
    notice = `<b style="color: var(--text-display);">This computer is in this team but isn't linked to your login.</b><br>
      Change its settings on the computer itself, or run <code>agc login</code> there to manage it from here too.
      <div style="${NOTICE_ACTIONS}">${localSettings}</div>`;
  } else {
    notice = `<b style="color: var(--text-display);">No computer is linked to your login in this team yet.</b><br>
      Install AgentCollision on a computer and run <code>agc login</code>. Its settings then appear here.
      <div style="${NOTICE_ACTIONS}"><a href="#" class="btn btn-sm" data-page-link="machines">Machines</a></div>`;
  }
  host.innerHTML = `<div style="${NOTICE}">${notice}</div><div id="${FORM_ID}"></div>`;
  const sw = host.querySelector("#ms-switch-team");
  if (sw) sw.addEventListener("click", async () => {
    sw.disabled = true;
    await switchLocalDaemonTeam(viewing);
    renderMachineSettingsPage();
  });
  renderMachineSettingsPreview(host.querySelector("#" + FORM_ID));
}

let started = false;
function start() {
  if (started) return;
  started = true;
  renderMachineSettingsPage();
}
function stop() {
  // Re-render on next activation so the form reflects current server state.
  started = false;
}

function bindLifecycle() {
  const section = document.querySelector('section.page[data-page="machine-settings"]');
  if (!section) return;
  if (section.classList.contains("active")) start();
  const obs = new MutationObserver((mutations) => {
    mutations.forEach((m) => {
      if (m.attributeName !== "class") return;
      if (section.classList.contains("active")) start();
      else stop();
    });
  });
  obs.observe(section, { attributes: true });
}

if (document.readyState === "loading") {
  document.addEventListener("DOMContentLoaded", bindLifecycle);
} else {
  bindLifecycle();
}
